<template>
  <LoginForm
    :fields="fieldsToShow"
    :logo-url="expandedLogoUrl"
    :logo-default="'https://generation-sis-assets.s3.eu-west-1.amazonaws.com/images/genex-logo-02.svg'"
    @forgotPasswordClick="forgotPassword"
    @submitted="nextState"
    :submitText="submitText"
    :data="{
      tenants: this.tenants,
    }"
    :error="error"
    ref="loginForm"
    help-text="Need help?"
    @help="help"
    @reset="reset"
    :state="state"
    :is-white-labeled="isWhiteLabeled" />
</template>

<script>
  import LoginForm from "@/components/auth/LoginForm";
  import User from "@/models/sis/User";
  import { TenantClient } from "@/clients/Tenant";
  import { LoginClient } from "@/clients/Login";
  import axios from "axios";
  import { mapGetters, mapState } from "vuex";
  import Store from "@/store";
  import GenexUtils from "@/utils/GenexUtils";
  import LMSUser from "@/models/lms/LMSUser";
  import Router from "@/router";

  export default {
    components: { LoginForm },
    name: "Login.view",
    data() {
      return {
        fieldsToShow: ["email"],
        submitText: "Continue",
        state: "email",
        tenants: [],
        error: null,
        hasAccess: true,
      };
    },
    props: {
      tenant: {
        type: String,
        default: null,
      },
    },
    mounted() {
      this.fetchSluggedTenant();
      if (this.$store.state.auth.needs_to_accept === true) {
        this.$buefy.dialog.confirm({
          title: this.$store.state.auth.terms_updated ? "Privacy Policy Updated" : "Privacy Policy",
          message: `<p>To ensure compliance with data protection laws, we are asking you to take a few moments to read and accept our ${
            this.$store.state.auth.terms_updated ? "updated" : ""
          } privacy policy.</p>
          <br/><p>By clicking Accept, you agree to our <a target="_blank" href="https://generation-sis-assets.s3.eu-west-1.amazonaws.com/downloads/GenEx+Privacy+Policy+.pdf">GenEx Privacy Policy</a>.</p>`,
          cancelText: "Cancel",
          confirmText: "Accept",
          type: "is-primary",
          onConfirm: () => {
            User.Accept(this.$store.state.user.id).then(() => {
              this.$store.state.auth.needs_to_accept = false;
              this.$store.state.user.accepted_terms_at = new Date();
              this.$nextTick(() => {
                // window.location.href = sessionStorage.getItem('redirectPath') || '/'
                this.$router.replace(sessionStorage.getItem("redirectPath") || "/");
                sessionStorage.removeItem("redirectPath");
              });
            });
          },
        });
      }
      this.loginSupport();
    },
    computed: {
      ...mapState("auth", ["needs_to_accept"]),
      //map tenant id and rename it to current_tenant_id
      ...mapState("tenant", {
        current_tenant_id: state => state.id,
      }),
      ...mapGetters("tenant", ["expandedLogoUrl", "isWhiteLabeled"]),
    },
    watch: {
      async state(name) {
        if (name === "password" && !this.hasAccess) {
          await this.reset();
        }
      },
      needs_to_accept(newVal) {
        if (newVal === true) {
          this.$store.dispatch("loader/hide");
          this.$buefy.dialog.confirm({
            title: this.$store.state.auth.terms_updated ? "Privacy Policy Updated" : "Privacy Policy",
            message: `<p>To ensure compliance with data protection laws, we are asking you to take a few moments to read and accept our ${
              this.$store.state.auth.terms_updated ? "updated" : ""
            } privacy policy.</p>
          <br/><p>By clicking Accept, you agree to our <a target="_blank" href="https://generation-sis-assets.s3.eu-west-1.amazonaws.com/downloads/GenEx+Privacy+Policy+.pdf">GenEx Privacy Policy</a>.</p>`,
            cancelText: "Cancel",
            confirmText: "Accept",
            type: "is-primary",
            onCancel: () => {
              this.$store.state.auth.needs_to_accept = false;
            },

            onConfirm: () => {
              User.Accept(this.$store.state.user.id).then(() => {
                this.$store.state.auth.needs_to_accept = false;
                this.$store.state.user.accepted_terms_at = new Date();
                this.$nextTick(() => {
                  this.$router.replace(sessionStorage.getItem("redirectPath") || "/");
                  sessionStorage.removeItem("redirectPath");
                  // window.location.href = "/"
                });
              });
            },
          });
        }
      },
    },
    methods: {
      async checkUserActive(user) {
        if (user.active !== 1 && !user.is_super_admin) {
          this.$buefy.toast.open({
            message: "Your access has been suspended. Please contact your organisation for assistance.",
            type: "is-danger",
            duration: 3000,
          });
          await GenexUtils.logoutAndPurgeUser();
          this.hasAccess = false;
          return;
        } else {
          this.hasAccess = true;
        }
      },
      getTenantQuery() {
        if (isNaN(this.tenant)) {
          return { slug: this.tenant };
        } else {
          return { id: this.tenant };
        }
      },
      async fetchSluggedTenant() {
        if (!this.tenant) {
          this.clearTenantData();
          return;
        }
        const query = this.getTenantQuery();
        const [error, { data }] = await this.handleResponse(() => TenantClient.getTenants(query));
        if (error) {
          this.handleError(error);
          return;
        }
        if (data.length === 0) {
          this.clearTenantData();
          await this.$router.push({ name: "Login" });
          return;
        }
        this.setTenantData(data[0]);
      },
      clearTenantData() {
        this.$store.state.auth.tenantId = null;
        this.$store.state.tenant.storage = null;
        this.$store.state.tenant.uuid = null;
        this.$store.state.tenant.school_name = null;
        this.$store.state.tenant.is_white_labeled = null;

        this.$cookies.remove("tenant_is_white_labeled");
        this.$cookies.remove("tenant_school_name");
        this.$cookies.remove("tenant_storage");
        this.$cookies.remove("tenant_uuid");
        this.$cookies.remove("tenant_id");
        this.$cookies.remove("tenant_slug");
        this.$cookies.remove("tenant_isd");
      },
      async reset() {
        this.data = {};
        this.error = null;
        this.state = "email";
        this.submitText = "Continue";
        this.fieldsToShow = ["email"];
        await this.$store.dispatch("loader/hide");
      },
      help() {
        //open email to support with subject "Login Help" and body "I need help logging in"
        window.open(`mailto:${process.env.VUE_APP_SUPPORT_EMAIL}?subject=Login Help&body=I need help logging in`);
      },
      async getTenants(email) {
        return this.handleResponse(() => TenantClient.getTenants({ email: encodeURIComponent(email) }));
      },
      async nextState({ email, password, tenant_id }) {
        try {
          if (this.state === "email") {
            if (this.$store.getters["tenant/isCentral"]) {
              this.state = "password";
              this.fieldsToShow = [
                {
                  name: "email",
                  disabled: true,
                },
                "password",
                "forgot password",
                "reset",
              ];
              this.submitText = "Login";
              await this.$store.dispatch("loader/hide");
              return;
            }
            await this.$store.dispatch("loader/show");
            const [error, { data }] = await this.getTenants(email);
            if (data && data?.status === 403) {
              // Re-direct user if they are a learner to the LMS login page
              await this.$store.dispatch("toast/createToast", {
                message: "Redirecting to LMS login page",
              });
              window.open(process.env.VUE_APP_LMS_FRONTEND_URL + "login" + "?email=" + email, "_self");
              return;
            }
            if (error) {
              if (error.response?.data?.message)
                await this.$store.dispatch("toast/createToast", {
                  message: error.response?.data?.message,
                });
              else this.handleError(error);

              await this.$store.dispatch("loader/hide");
              return;
            }
            this.tenants = data;
            if (this.tenants.map(t => t.id).includes(this.current_tenant_id)) {
              this.tenants = this.tenants.filter(t => t.id === this.current_tenant_id);
            }
            if (this.tenants.length === 0) {
              await this.$store.dispatch("toast/createToast", {
                message: "Unable to find your account. Please contact your school campus.",
              });
              await this.reset();
              await this.$store.dispatch("loader/hide");
              return;
            }
            if (this.tenants.length === 1) {
              await this.checkUserActive(this.tenants[0]);
              this.setTenantData(this.tenants[0]);
              this.state = "password";
              this.fieldsToShow = [
                {
                  name: "email",
                  disabled: true,
                },
                "password",
                "forgot password",
                "reset",
              ];
              this.submitText = "Login";
              await this.$store.dispatch("loader/hide");
              return;
            }
            this.state = "tenants";
            this.fieldsToShow = [
              {
                name: "email",
                disabled: true,
              },
              "tenants",
              "reset",
            ];
            this.submitText = "Select";
            await this.$store.dispatch("loader/hide");
            return;
          }
          if (this.state === "tenants") {
            this.cookieRemoval("tenant_slug");
            this.cookieRemoval("tenant_is_white_labeled");
            this.state = "password";
            this.submitText = "Login";
            this.fieldsToShow = [
              {
                name: "email",
                disabled: true,
              },
              {
                name: "tenants",
                disabled: true,
              },
              "password",
              "forgot password",
              "reset",
            ];
            const tenant = this.tenants.find(t => t.id === tenant_id);
            this.setTenantData(tenant);
            return;
          }
          if (this.state === "password") {
            await this.login({ email, password });
          }
        } catch (error) {
          await this.$store.dispatch("toast/createToast", {
            message: "Something went wrong. Please try again.",
          });
          //TEMP CLEARING COOKIES https://trello.com/c/xcM7W6L7/1890-sis-bug-p1-login-cors-error-for-some-users
          this.$cookies.keys().forEach(key => {
            this.$cookies.remove(key);
          });
        }
      },
      cookieRemoval(key) {
        this.$cookies.remove(key);
      },
      forgotPassword({ email, tenant_id }) {
        let slug =
          this.$cookies.get("tenant_slug") && this.$cookies.get("tenant_is_white_labeled")
            ? `/${this.$cookies.get("tenant_slug")}`
            : "";
        this.$router.push({
          path: `/forgot-password${slug}`,
          query: {
            email,
            tenant_id,
          },
        });
      },
      async setTenantData(tenant) {
        if (tenant.is_white_labeled === 1 && tenant.slug) {
          this.$router.replace(`/login/${tenant.slug}`);
        } else {
          if (this.$router.history.current.path !== "/login") {
            this.$router.replace("/login");
          }
        }
        if (tenant.active === 1 || tenant.is_super_admin) {
          this.$store.state.auth.tenantId = tenant.id;
          this.$store.state.auth.lmsTenantId = tenant.lms_tenant_id;
          this.$store.state.tenant.storage = tenant.storage;
          this.$store.state.tenant.uuid = tenant.uuid;
          this.$store.state.tenant.school_name = tenant.school_name;
          this.$store.state.tenant.is_white_labeled = tenant.is_white_labeled;

          this.cookieStorage("tenant_is_white_labeled", 1);
          this.cookieStorage("tenant_school_name", tenant.school_name);
          this.cookieStorage("tenant_storage", JSON.stringify(tenant.storage));
          this.cookieStorage("tenant_uuid", tenant.uuid);
          this.cookieStorage("tenant_id", tenant.id);
          this.cookieStorage("tenant_slug", tenant.slug);
          this.cookieStorage("tenant_isd", tenant.ISD);
        }
      },
      async login({ email, password }) {
        this.cookieRemoval("access_token");
        await this.$store.dispatch("loader/show");

        let clientId = process.env.VUE_APP_TENANT_CLIENT_ID;
        let clientSecret = process.env.VUE_APP_TENANT_CLIENT_SECRET;
        if (this.$store.getters["tenant/isCentral"]) {
          clientId = process.env.VUE_APP_CENTRAL_CLIENT_ID;
          clientSecret = process.env.VUE_APP_CENTRAL_CLIENT_SECRET;
        }

        try {
          const response = await LoginClient.login(
            {
              username: email,
              password: password,
              grant_type: "password",
              client_id: clientId,
              client_secret: clientSecret,
            },
            this.$store.state.auth.tenantId,
          );
          if (response.data.chat) {
            await this.$store.dispatch("chat/setChatDetails", response.data.chat);
          }
          this.saveCookie(response.data.access_token, response.data.uuid);
        } catch (err) {
          await this.$store.dispatch("loader/hide");
          if (err.response.data && err.response.data.message) {
            this.error = err.response.data.message;
            this.fieldsToShow.push("help");
          } else {
            this.handleError(err);
          }
        }
      },
      cookieStorage(key, data) {
        this.$cookies.set(key, data);
      },
      saveCookie(access_token, uuid) {
        this.$store.dispatch("auth/login", access_token).then(() => {
          let defaultHeaders = {
            "X-Requested-With": "XMLHttpRequest",
            Accept: "application/json",
            "Content-Type": "application/json",
          };
          axios.interceptors.request.handlers = [];
          if (access_token) {
            this.cookieStorage("access_token", access_token);

            localStorage.setItem("uuid", uuid);
            this.$store.state.auth.accessToken = access_token;
            defaultHeaders["Authorization"] = "Bearer " + access_token;
          }

          if (this.$store.state.auth.tenantId) {
            defaultHeaders["X-Tenant"] = this.$store.state.auth.tenantId;
          }

          axios.defaults.headers.common = defaultHeaders;

          this.track("Login", {
            succeeded_at: new Date(),
          });
          // this.$router.replace(sessionStorage.getItem("redirectPath") || "/");

          sessionStorage.removeItem("redirectPath");
          const test = 1;
          if (test == 1) {
            User.Check()
              .then(({ response }) => {
                GenexUtils.saveAuthDetails(response);

                if (response.data.data.attributes.lms_user_id) {
                  User.LoginIntoLMS(response.data.data.attributes.lms_user_id)
                    .then(result => {
                      const lmsAccessToken = result.response.data.access_token;
                      Store.state.auth.lmsAccessToken = lmsAccessToken;
                      GenexUtils.setCookie("lmsAccessToken", lmsAccessToken, 1);

                      const lmsTenantId = result.response.data.lms_tenant_id;
                      Store.state.auth.lmsTenantId = lmsTenantId;
                      GenexUtils.setCookie("lmsTenantId", lmsTenantId, 1);

                      LMSUser.FetchMe().then(() => {
                        const lmsUserDetails = LMSUser.query().first();
                        Store.state.auth.lmsUserDetails = lmsUserDetails;
                        GenexUtils.setCookie("lmsUserDetails", lmsUserDetails, 1);
                      });

                      Store.dispatch("auth/updateAuthenticated", true).then(() => {
                        // return next();
                        Router.replace(sessionStorage.getItem("redirectPath") || "/");
                      });

                      // if (!VueCookies.get("SecondRefresh")) {
                      //   GenexUtils.setCookie("SecondRefresh", true, 365);
                      //   window.location.href = "/";
                      // }
                      //
                      // this.$router.replace(sessionStorage.getItem("redirectPath") || "/");
                    })
                    .catch(() => {
                      Router.replace(sessionStorage.getItem("redirectPath") || "/");
                    });
                } else {
                  Router.replace(sessionStorage.getItem("redirectPath") || "/");
                }
              })
              .catch(() => {
                GenexUtils.logoutAndPurgeUser();
              });
          }
          // window.location.href = "/"
        });

        if (this.$cookies.isKey(`is_fresh_login`)) {
          this.cookieRemoval(`is_fresh_login`);
        }
      },
      loginSupport() {
        if (
          this.$route.query.supportLoginTenantId &&
          this.$route.query.supportLoginToken &&
          this.$route.query.supportLoginUuid
        ) {
          this.cookieRemoval("access_token");
          this.$store.dispatch("loader/show");
          TenantClient.getTenants({
            id: parseInt(this.$route.query.supportLoginTenantId),
          })
            .then(({ data }) => {
              if (data.length > 0) {
                this.$store.state.auth.tenantId = data[0].id;
                this.$store.state.tenant.storage = data[0].storage;
                this.$store.state.tenant.uuid = data[0].uuid;
                this.$store.state.tenant.school_name = data[0].school_name;
                this.$store.state.tenant.is_white_labeled = data[0].is_white_labeled;

                if (data[0].is_white_labeled === 1) {
                  this.cookieStorage("tenant_is_white_labeled", 1);
                }

                this.cookieStorage("tenant_school_name", data[0].school_name);
                this.cookieStorage("tenant_storage", JSON.stringify(data[0].storage));
                this.cookieStorage("tenant_uuid", data[0].uuid);
                this.cookieStorage("tenant_id", data[0].id);
                this.cookieStorage("tenant_slug", data[0].slug);
              } else {
                return Promise.reject("no-tenants");
              }
            })
            .then(() => {
              this.saveCookie(this.$route.query.supportLoginToken, this.$route.query.supportLoginUuid);
            })
            .catch(err => {
              this.$store.dispatch("loader/hide");
              if (err === "no-tenants") {
                this.$store.dispatch("toast/createToast", {
                  message: "Unable to find your account. Please contact your school campus.",
                });
                return;
              }
              if (err.response.status.toString()[0] === "4") {
                this.$store.dispatch("toast/createToast", {
                  message: "Please check that your login details are correct.",
                });
              } else {
                this.handleError(err);
              }
            });
        }
      },
    },
  };
</script>
